import Echo from '@ably/laravel-echo';

import { Loggable } from '~/core/shared/utils/LoggableService';
import { Nullable } from '../../../../types';
import { WebsocketEventHandler, WebsocketServiceChannelConfig } from './types';

type EventCallback = (data: any) => void;

/**
 * Owns the desired-subscription state for a single channel (which events have
 * listeners), independent of any particular connection. The live channel is
 * transient: it is created on `attach(echo)` and dropped on `detach()`, so the
 * same desired state can be re-bound whenever the underlying Echo instance is
 * (re)established.
 *
 * A single dispatcher is registered per event and fans out to all listener
 * callbacks, so multiple components can listen to the same event and unmounting
 * one leaves the others subscribed (unlike a per-add `listen` + `stopListening`
 * without a handler, which duplicates delivery and removes all handlers at once).
 */
export class WebsocketServiceChannel<Channel extends object> extends Loggable {
  private events: Record<string, Set<EventCallback>> = {};

  private dispatchers: Record<string, WebsocketEventHandler> = {};

  private channelConnection: Nullable<Channel> = null;

  private echo: Nullable<Echo> = null;

  constructor(
    private readonly channelName: string,
    private readonly config: WebsocketServiceChannelConfig<Channel>,
    private readonly onChannelCleanUp: (channelName: string) => void,
  ) {
    super('websocket-service-channel');
  }

  private get customLogger() {
    return {
      ...this.logger,
      log: (...msgs: any[]) => {
        this.logger.log(...msgs, `\nchannel: ${this.channelName}`);
      },
    };
  }

  /**
   * Bind this channel's desired events onto a (new) connection.
   */
  public attach(echo: Echo): void {
    this.echo = echo;
    this.channelConnection = this.config.connect(echo, this.channelName);
    Object.keys(this.events).forEach((eventName) =>
      this.bindDispatcher(eventName),
    );
  }

  /**
   * Unbind from the current connection but keep the desired-event registry so it
   * can be re-attached to a future connection.
   */
  public detach(): void {
    if (this.channelConnection) {
      Object.entries(this.dispatchers).forEach(([eventName, dispatcher]) =>
        this.config.stopListening(
          this.channelConnection as Channel,
          eventName,
          dispatcher,
        ),
      );
      if (this.echo) {
        this.config.leave(this.echo, this.channelName);
      }
    }
    this.dispatchers = {};
    this.channelConnection = null;
    this.echo = null;
  }

  private bindDispatcher(eventName: string): void {
    if (!this.channelConnection || this.dispatchers[eventName]) {
      return;
    }
    const dispatcher: WebsocketEventHandler = (data) => {
      this.customLogger.log('event received', eventName, data);
      this.events[eventName]?.forEach((callback) => callback(data));
    };
    this.dispatchers[eventName] = dispatcher;
    this.config.listen(this.channelConnection, eventName, dispatcher);
  }

  private unbindDispatcher(eventName: string): void {
    const dispatcher = this.dispatchers[eventName];
    if (dispatcher && this.channelConnection) {
      this.config.stopListening(this.channelConnection, eventName, dispatcher);
    }
    delete this.dispatchers[eventName];
  }

  public addEventCallback<T>(
    eventName: string,
    callback: (data: T) => void,
  ): () => void {
    if (!this.events[eventName]) {
      this.events[eventName] = new Set();
    }
    this.customLogger.log('adding listener', eventName);
    this.events[eventName].add(callback as EventCallback);
    this.bindDispatcher(eventName);

    return () => {
      const listeners = this.events[eventName];
      if (!listeners) {
        return;
      }
      this.customLogger.log('removing listener', eventName);
      listeners.delete(callback as EventCallback);

      if (listeners.size === 0) {
        this.unbindDispatcher(eventName);
        delete this.events[eventName];
      }
      if (Object.keys(this.events).length === 0) {
        this.onChannelCleanUp(this.channelName);
      }
    };
  }

  public destroy(): void {
    this.customLogger.log('destroying');
    this.detach();
    this.events = {};
  }
}
